/**
 * s-motion — Prestige-style reveal motion (27 Oct 2026, merchant: "like Prestige: slow, very smooth
 * scroll and reveal animations").
 *
 * The look lives in config/styles.css ("Prestige-style reveal motion"); this script only decides *when*:
 *   - Each reveal target gets .is-revealed as it enters the viewport (on load for what is already visible).
 *   - Targets arriving together are staggered 110ms apart in reading order (top to bottom, start to end),
 *     so a row of cards or a heading block unfolds one piece after another.
 *   - A card's artwork reveals with its card (plus a short beat) rather than on its own.
 *   - Hairlines (placards, ruled grids) get .is-drawn and draw from start to finish.
 *
 * html.motion-ready (set in the layout) holds targets hidden from the first paint; this script adds
 * .motion-live to show it is running (otherwise a CSS failsafe reveals everything after 4s).
 * Under prefers-reduced-motion everything is shown at once.
 * Also: text-free loading dots on ordinary form buttons.
 */

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const root = document.documentElement;

// Keep in sync with "Prestige-style reveal motion" in config/styles.css.
const TEXT_TARGETS = [
  '#main-content .placard',
  '#main-content :is(h1, h2)',
  '#main-content :is(h1, h2) ~ :is(p, a, form)',
  '#main-content .section-header > *',
].join(', ');
// Every list row / card (product grids, index rows, benefits, steps, reviews, journal) and FAQ rows.
const CARD_TARGETS = '#main-content :is(ul, ol)[role="list"]:not(nav *, form *, dialog *) > li, #main-content details.accordion';
const IMAGE_TARGETS = '#main-content .mat img';
const LINE_TARGETS = '.ruled-grid, .ruled-grid > *, .ruled-split, .placard';

const STAGGER_MS = 110;
const MAX_STAGGER_STEPS = 7;
const CLEANUP_MS = 3400;

function revealNow(el, delay) {
  if (el.classList.contains('is-revealed')) return;
  el.style.setProperty('--reveal-delay', `${delay}ms`);
  el.classList.add('is-revealed');
  setTimeout(() => el.style.removeProperty('--reveal-delay'), delay + CLEANUP_MS);
  // A card brings its artwork with it, a beat later.
  if (el.matches(CARD_TARGETS)) {
    el.querySelectorAll('.mat img').forEach((img) => {
      revealObserver.unobserve(img);
      revealNow(img, delay + 120);
    });
  }
}

const revealObserver = new IntersectionObserver(
  (entries) => {
    const arriving = entries
      .filter((entry) => entry.isIntersecting)
      .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top || a.boundingClientRect.left - b.boundingClientRect.left);
    arriving.forEach((entry, index) => {
      revealObserver.unobserve(entry.target);
      revealNow(entry.target, Math.min(index, MAX_STAGGER_STEPS) * STAGGER_MS);
    });
  },
  { rootMargin: '0px 0px -8% 0px', threshold: 0 }
);

const lineObserver = new IntersectionObserver(
  (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      entry.target.classList.add('is-drawn');
      lineObserver.unobserve(entry.target);
    }
  },
  { rootMargin: '0px 0px -8% 0px', threshold: 0 }
);

function bindOnce(el, key, observer) {
  if (el.dataset[key] !== undefined) return;
  el.dataset[key] = '';
  observer.observe(el);
}

function scan() {
  document.querySelectorAll(TEXT_TARGETS).forEach((el) => bindOnce(el, 'revealBound', revealObserver));
  document.querySelectorAll(CARD_TARGETS).forEach((el) => bindOnce(el, 'revealBound', revealObserver));
  document.querySelectorAll(IMAGE_TARGETS).forEach((img) => {
    if (img.closest('s-slideshow') || img.closest(CARD_TARGETS)) return; // handled by the slideshow / its card
    bindOnce(img, 'revealBound', revealObserver);
  });
  document.querySelectorAll('s-slideshow').forEach((el) => bindOnce(el, 'revealBound', revealObserver));
  document.querySelectorAll(LINE_TARGETS).forEach((el) => bindOnce(el, 'lineBound', lineObserver));
}

/* ---------- form buttons: text-free loading dots ---------- */
function setupFormLoading() {
  document.addEventListener('submit', (event) => {
    if (event.defaultPrevented) return;
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || (form.method || '').toLowerCase() !== 'post') return;
    const button = event.submitter || form.querySelector('[type="submit"]');
    if (!(button instanceof HTMLElement) || !form.checkValidity()) return;
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');
  });
  // Coming back via the back button restores the page from cache: clear any loaders left on it.
  window.addEventListener('pageshow', () => {
    document.querySelectorAll('.is-loading[aria-busy="true"]').forEach((el) => {
      el.classList.remove('is-loading');
      el.removeAttribute('aria-busy');
    });
  });
}

function showEverything() {
  root.classList.remove('motion-ready');
}

function init() {
  root.classList.remove('motion-pending');
  document.querySelectorAll('.stagger-fade-in').forEach((list) => list.classList.remove('stagger-fade-in'));
  setupFormLoading();
  if (reduceMotion.matches) {
    showEverything();
    return;
  }
  root.classList.add('motion-live');

  const safeScan = () => {
    try {
      scan();
    } catch (error) {
      console.error('[s-motion] reveal failed; showing everything', error);
      showEverything();
    }
  };
  safeScan();

  // Content rendered later (recommendations, recently viewed, filtered grids) gets the same reveal.
  let queued = false;
  const queueScan = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      safeScan();
    });
  };
  document.addEventListener('motion:refresh', queueScan);
  const main = document.getElementById('main-content');
  if (main) new MutationObserver(queueScan).observe(main, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
